/* CZARD — Request Access gate. Self-contained.

   ── What this gates, as of v27 ──

   The collection page's manifesto button, and nothing else. The home scene
   used to be behind this too: every link out of #golden-fade-wrapper opened
   the form first and was replayed after the submit. That has been removed —
   the scene's pill reads "Discover" and navigates straight through — so the
   only thing left that opens this form is a link carrying
   data-czard-access-link. See isGated() in section 6.

   Rewritten to depend on nothing but itself. The previous version was split
   across four files — this script, a snippet for the markup, a stylesheet,
   and a click handler inside sections/hero-houseofczard.liquid — and every
   one of them had to be current for the gate to work. A single stale file
   produced a silent, total failure that looked identical from the outside
   whichever file it was. That is the bug worth fixing, so:

     - the modal markup is built here, not rendered from Liquid
     - the styles are injected here, not loaded from an asset
     - clicks are caught on document at capture phase, so no other script's
       handler can navigate first, however old that script is
     - the button label is enforced on an animation frame, so nothing else
       repainting it can win

   Push this one file and the whole flow works. Nothing else is required.

   Scope, stated plainly: the unlock is per browser, in localStorage with a
   cookie mirror. It is a courtesy, not an authentication boundary — the
   pages behind it stay reachable by direct URL. */
(function () {
  'use strict';

  // The payload shape is the one 24 introduced and is unchanged here: user_id
  // (the WhatsApp number) replaces dedupeKey, and consent is sent as
  // user_opt_in. The `v` column in the sheet is how a row from either era is
  // told apart — and which build wrote it, which is the quickest way to see a
  // browser is still serving a cached copy of this file.
  var VERSION = 27;

  /* STORE_KEY and COOKIE_NAME are read by one other file:
     snippets/czard-product-guard.liquid, which redirects a visitor off a
     product page when the unlock is absent. It cannot call in here — it runs
     in <head>, before this file has loaded — so it holds its own copy of
     these two names and of the `granted` test below.

     Change either one and change it there too, or every product page starts
     bouncing visitors who have in fact been through the form. */
  var STORE_KEY = 'czard:access';
  var COOKIE_NAME = 'czard_access';
  var CHANGE_EVENT = 'czard:access-change';

  /* ============================================================ 1. STATE */

  var cached = null;

  function lsGet () { try { return localStorage.getItem(STORE_KEY); } catch (e) { return null; } }
  function lsSet (v) { try { localStorage.setItem(STORE_KEY, v); return true; } catch (e) { return false; } }

  function ckGet () {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function ckSet (v) {
    try {
      document.cookie = COOKIE_NAME + '=' + encodeURIComponent(v) +
        '; expires=' + new Date(Date.now() + 31536e6).toUTCString() + '; path=/; SameSite=Lax';
      return ckGet() === v;
    } catch (e) { return false; }
  }

  function parse (raw) {
    if (!raw) return null;
    try { var p = JSON.parse(raw); return (p && p.granted) ? p : null; } catch (e) { return null; }
  }

  function readState () {
    if (cached) return cached;
    cached = parse(lsGet()) || parse(ckGet());
    return cached;
  }

  function granted () { return !!readState(); }

  function writeState (profile) {
    var state = {
      granted: true, at: new Date().toISOString(),
      name: profile.name, email: profile.email, phone: profile.phone
    };
    cached = state;
    var raw = JSON.stringify(state);
    var okLS = lsSet(raw), okCK = ckSet(raw);
    console.info('[czard-access] saved →', okLS ? 'localStorage' : '', okCK ? 'cookie' : '',
                 (!okLS && !okCK) ? 'NOWHERE — storage is blocked, the unlock will not survive a reload' : '');
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { granted: true } }));
    return state;
  }

  /* ======================================================== 2. WEBENGAGE */

  var queue = [];

  /* Two different WebEngage scripts can be on the page and they are not
     equivalent:

       webengage-shopify-sdk.min.js  — the Shopify app. Sends its own commerce
                                       events and auto-captures form fields.
       webengage-min-v-6.0.js        — the Web SDK. The only one that provides
                                       webengage.user.setAttribute.

     Requiring user.setAttribute meant that with only the app present, this
     returned null forever and every call queued up and was never sent, in
     total silence. Anything usable now counts as ready, and each call site
     checks for the specific method it needs. */
  function sdk () {
    var w = window.webengage;
    if (!w) return null;
    if (typeof w.track === 'function') return w;
    if (w.user && typeof w.user.setAttribute === 'function') return w;
    return null;
  }

  function weReport () {
    var w = window.webengage;
    return {
      present: !!w,
      sdkVersion: w && w.__v,
      hasTrack: !!(w && typeof w.track === 'function'),
      hasUserSetAttribute: !!(w && w.user && typeof w.user.setAttribute === 'function'),
      hasUserLogin: !!(w && w.user && typeof w.user.login === 'function'),
      unsentCalls: queue.length
    };
  }

  function withSDK (fn) {
    var w = sdk();
    if (w) { try { fn(w); } catch (e) { console.warn('[czard-access] WebEngage call failed', e); } return; }
    queue.push(fn);
  }

  // The SDK arrives late, or never (an ad blocker will stop it outright).
  // Either way the visitor is let through — analytics must not gate anyone.
  setInterval(function () {
    if (!queue.length) return;
    var w = sdk();
    if (!w) return;
    var q = queue.splice(0, queue.length);
    for (var i = 0; i < q.length; i++) {
      try { q[i](w); } catch (e) { console.warn('[czard-access] WebEngage call failed', e); }
    }
  }, 1000);

  function track (name, data) {
    withSDK(function (w) { if (typeof w.track === 'function') w.track(name, data || {}); });
  }

  function identify (p) {
    withSDK(function (w) {
      if (!w.user || typeof w.user.setAttribute !== 'function') {
        console.warn(
          '[czard-access] webengage.user.setAttribute is unavailable, so no user ' +
          'attributes were written. The Web SDK (webengage-min-v-6.0.js) is not ' +
          'loaded — either the app embed does not include it, or an ad blocker is ' +
          'stopping it. The submitted values still go out on the event below, but ' +
          'they will land as event attributes rather than on the user profile.',
          weReport()
        );
        return;
      }

      var parts = String(p.name || '').trim().split(/\s+/);
      var first = parts.shift() || '', last = parts.join(' ');
      /* The WhatsApp number in E.164 is the user id — the CUID every event
         and attribute below is filed under. It was the email address until
         now, which is what WebEngage were still seeing on their side.

         The number is the key the rest of this file already uses: the sheet's
         user_id column and the written-ledger both moved to it, and the note
         by userId() says WebEngage is keyed on it too. That was the intent
         and this line is what makes it true — with the address here, the CRM
         and the sheet disagreed about who a row was, which is precisely the
         cross-device dedupe those two were meant to share.

         Chosen for the same reasons stated there: WhatsApp is the channel the
         collection is followed up on, one person routinely has several
         addresses and only ever one number, and login() takes the value
         verbatim. It is validated E.164 by the time it reaches here — the
         submit cannot pass otherwise — so this is never empty and never a
         differently-formatted copy of the same number.

         Worth knowing when this ships: WebEngage does not re-key an existing
         profile. Anyone who submitted before is filed under their email and
         stays there; their next submission creates a second profile under
         their number. Merging the historical ones is a job for the WebEngage
         side, not this file. */
      if (typeof w.user.login === 'function') w.user.login(p.phone);
      w.user.setAttribute('we_first_name', first);
      if (last) w.user.setAttribute('we_last_name', last);
      // Still set, and still how the email channel reaches them — it is simply
      // an attribute on the profile now rather than the identity of it.
      w.user.setAttribute('we_email', p.email);
      w.user.setAttribute('we_phone', p.phone);      // E.164, and the CUID above
      /* All four channel opt-ins, set together when the consent box is
         ticked, and never otherwise — the flag is threaded through from the
         form rather than assumed, because opting a number in without consent
         is what puts a WhatsApp business account at risk.

         Three of these are documented system attributes and take effect
         immediately. we_push_opt_in is the fourth channel WebEngage lists
         (SMS, Web Push, Email, WhatsApp) but does not document a setter for;
         it is set here for completeness and is harmlessly ignored if the
         name is not recognised. Web push reachability is decided by the
         browser's own permission prompt in any case — a ticked box cannot
         grant it, so treat that channel as pending until WebEngage's prompt
         has actually been accepted. */
      if (p.consent) {
        w.user.setAttribute('we_email_opt_in', true);
        w.user.setAttribute('we_sms_opt_in', true);
        w.user.setAttribute('we_whatsapp_opt_in', true);
        // The fourth channel. The comment above said this was set and the log
        // below said so too, but the line itself was missing — so the console
        // reported an opt-in that had never been written. Set for real now;
        // an unrecognised attribute name is ignored harmlessly either way.
        w.user.setAttribute('we_push_opt_in', true);
        console.info('[czard-access] opt-ins set: email, sms, whatsapp, push');
      }

      w.user.setAttribute('Access Requested', true);
      w.user.setAttribute('Access Requested On', new Date());
      // The number, because that is the id the profile was written under —
      // logging the address here is what made an email-keyed CUID look right.
      console.info('[czard-access] user attributes sent to WebEngage for', p.phone,
                   '(user id) —', p.email);
    });
  }

  /* ====================================================== 3. GOOGLE SHEET

     A browser cannot write to a spreadsheet. What it can do is POST to a
     Google Apps Script Web App bound to one, which appends the row itself —
     so the URL configured in the theme editor is the script's /exec
     deployment, not the sheet's address.

     Two details in the request are load-bearing:

       Content-Type: text/plain — keeps this a "simple" CORS request, so no
       preflight OPTIONS is sent. An Apps Script web app does not answer
       preflight, and with any other content type the POST never arrives.

       keepalive: true — the page navigates roughly a second after submit,
       which would otherwise cancel the request in flight. The payload is a
       few hundred bytes, far inside the 64KB keepalive ceiling.

     ── Why this is not just one sendBeacon call ──

     It used to be, and on Brave that lost every lead. Brave's Shields
     neuter navigator.sendBeacon: the call RETURNS TRUE and the request is
     never sent. The old code took that true as proof of delivery, returned
     early — so the fetch below was never reached — logged "row queued", and
     navigated a second later. Nothing on the page could tell, and nothing
     did. Chrome and Safari sent it, so the sheet looked healthy.

     The rule that follows from that, and the one thing to preserve if this
     is ever rewritten: sendBeacon's return value is not evidence. Only a
     settled fetch is, and even then only that the request reached the
     network — the sheet is still the only proof the row was appended.

     So delivery is now: write to an outbox first, then try fetch (which
     reports back: an opaque response means it went, a rejection means
     something blocked it), then beacon, then a GET image, and only drop the
     outbox entry once fetch has confirmed. Whatever is left over is retried
     on the next page load. A blocked first attempt costs a few minutes, not
     the lead. */

  var OUTBOX_KEY = 'czard:access:outbox';
  var SEND_DEADLINE = 3500;   // never hold the visitor longer than this
  var MAX_ATTEMPTS = 5;
  var OUTBOX_TTL = 3 * 864e5; // three days, then it is stale news
  /* A pageshow can arrive seconds after a submit — a bfcache restore, or the
     redirect itself — and without this the entry is re-sent while the first
     attempt is still in the air, spending its retries in one burst. */
  var RETRY_GAP = 15e3;

  function uid () {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function outbox () {
    try {
      var raw = localStorage.getItem(OUTBOX_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) { return []; }
  }

  function outboxSave (list) {
    try {
      if (!list.length) localStorage.removeItem(OUTBOX_KEY);
      else localStorage.setItem(OUTBOX_KEY, JSON.stringify(list));
      return true;
    } catch (e) { return false; }
  }

  function outboxAdd (entry) {
    var list = outbox();
    list.push(entry);
    if (!outboxSave(list)) {
      console.warn('[czard-access] the outbox could not be written — localStorage is ' +
                   'blocked, so a failed send cannot be retried later.');
    }
  }

  function outboxDrop (id) {
    var list = outbox(), out = [];
    for (var i = 0; i < list.length; i++) if (list[i].id !== id) out.push(list[i]);
    outboxSave(out);
  }

  /* ─────────────────────────── the ledger: who has already been written ──

     Three things wrote the same person to the sheet more than once, and
     they are worth naming because each needs a different guard:

       1. Enter key repeat. The keydown handler calls submit() directly, so
          elGo.disabled — which only stops the button — never applied to it.
          Holding Enter in a field auto-repeated and fired a burst of
          identical submissions milliseconds apart. This is where rows came
          in threes and fours. Guarded by `submitting` in submit().

       2. A second visit with the unlock gone. The gate is per browser, in
          localStorage with a cookie mirror; clear either, or arrive in a
          private window, and the form opens again to someone already in the
          sheet. Guarded by this ledger, which is checked before any write.

       3. Retries. An unconfirmed send is retried from the outbox, so the
          same row can be POSTed more than once by design. Guarded by the
          per-attempt `id` — it is stable across retries, so the Apps Script
          can drop a repeat.

     The ledger is localStorage, so it shares the fate of the unlock: wiped
     storage wipes it too, and it knows nothing about the visitor's other
     browser or their phone. That is why every payload also carries
     user_id. Only the sheet can dedupe across devices — see the note on
     CzardAccess.sheet() for the few lines that do it. */

  var LEDGER_KEY = 'czard:access:written';
  var LEDGER_MAX = 50;

  /* The identity of a lead is the WhatsApp number, not the address.

     This was the normalised email until now. The number is the better key
     for this store: WhatsApp is the channel the collection is actually
     followed up on, one person routinely has several addresses and only
     ever one number, and it is the value WebEngage is keyed on too — so
     the sheet and the CRM agree about who a row is.

     That last part is only true because identify() in section 2 passes this
     same number to webengage.user.login. It passed the email for a while
     after the sheet had moved, and the two stores silently disagreed about
     who a person was for as long as it did. Move one, move both.

     Already E.164 by the time it arrives here, because toE164() runs
     before validation passes, so this is a normalisation in name only. It
     stays a function so there is still exactly one place to change if the
     key ever moves again.

     Note for the Apps Script: this is a leading-'+' string and Google
     Sheets reads a leading + as the start of a formula. Write it with
     setValue on a cell preformatted as plain text, or prefix it with an
     apostrophe, or the id lands as the number 919876543210 and stops
     matching anything. */
  function userId (phone) { return String(phone || '').trim(); }

  function ledger () {
    try {
      var raw = localStorage.getItem(LEDGER_KEY);
      var o = raw ? JSON.parse(raw) : {};
      return (o && typeof o === 'object') ? o : {};
    } catch (e) { return {}; }
  }

  function ledgerSave (o) {
    try {
      // oldest out first, so a shared machine cannot grow this without bound
      var keys = Object.keys(o);
      if (keys.length > LEDGER_MAX) {
        keys.sort(function (a, b) { return (o[a].at || 0) - (o[b].at || 0); });
        for (var i = 0; i < keys.length - LEDGER_MAX; i++) delete o[keys[i]];
      }
      localStorage.setItem(LEDGER_KEY, JSON.stringify(o));
    } catch (e) {}
  }

  /* Ledgers written before the key moved from email to number are keyed on
     an address, so nothing looks them up again — they are simply dead
     weight that counts toward LEDGER_MAX and evicts live entries early.
     Dropped once, on the load after the upgrade, on the one thing that
     tells the two key shapes apart.

     No leads are put at risk by this: a purged entry means the ledger stops
     claiming that person was already written, and the worst case is one
     extra row for a returning visitor. The sheet's own user_id check is
     what catches that, which is why it is worth implementing there. */
  (function purgeEmailKeyedLedger () {
    var o = ledger(), touched = false;
    for (var k in o) {
      if (Object.prototype.hasOwnProperty.call(o, k) && k.indexOf('@') !== -1) {
        delete o[k];
        touched = true;
      }
    }
    if (touched) {
      ledgerSave(o);
      console.info('[czard-access] the written-ledger held email-keyed entries ' +
                   'from before user_id became the WhatsApp number. Dropped.');
    }
  })();

  function ledgerMark (key, id, confirmed) {
    if (!key) return;
    var o = ledger();
    var prev = o[key] || {};
    o[key] = { at: prev.at || Date.now(), id: id || prev.id, confirmed: !!confirmed || !!prev.confirmed };
    ledgerSave(o);
  }

  /* Has this person already been written? Confirmed means yes, definitively.
     Unconfirmed counts too while the outbox still holds that attempt — the
     retry will deliver it, and a second write would be the duplicate.

     Once the outbox has given up on it, this deliberately returns false:
     a lead that never landed is worth a second attempt far more than it is
     worth protecting from a duplicate. */
  function alreadyWritten (key) {
    var e = ledger()[key];
    if (!e) return false;
    if (e.confirmed) return 'confirmed';
    var list = outbox();
    for (var i = 0; i < list.length; i++) if (list[i].id === e.id) return 'pending';
    return false;
  }

  /* The endpoint is remembered, not only read.

     data-czard-sheet lives on the home scene's wrapper, so it is on exactly
     one page — and this script is sitewide. Without the cache a retry could
     only ever run on the home page, which is the one page the visitor has
     just been redirected away from: the outbox would sit full until they
     happened to come back. Cached, the collection page they land on drains
     it seconds later.

     The cached copy is refreshed on every home-page load, so a changed
     theme setting takes effect as soon as the visitor next sees the scene. */
  var SHEET_KEY = 'czard:access:endpoint';

  function sheetUrl () {
    var scene = document.getElementById('golden-fade-wrapper');
    var url = (scene && scene.getAttribute('data-czard-sheet')) || window.CZARD_SHEET_URL || '';
    url = String(url).trim();

    if (!url) {
      try { return localStorage.getItem(SHEET_KEY) || ''; } catch (e) { return ''; }
    }

    // The single most likely mistake, and it fails silently otherwise.
    if (url.indexOf('docs.google.com/spreadsheets') !== -1) {
      console.warn(
        '[czard-access] the Google Sheet setting holds a spreadsheet URL. That ' +
        'cannot be written to from a browser. Deploy the Apps Script as a Web App ' +
        'and paste its /exec URL instead. No row will be written until then.'
      );
      return '';
    }

    try { localStorage.setItem(SHEET_KEY, url); } catch (e) {}
    return url;
  }

  /* POST, and report back. no-cors because an Apps Script /exec URL answers
     with a 302 to script.googleusercontent.com and the redirected response
     carries no CORS headers — a cors-mode fetch rejects even when the row
     was written perfectly well. Opaque is unreadable but that is fine: what
     is wanted here is not the response body, it is the difference between
     "the request reached the network" and "something stopped it", and
     resolve-vs-reject carries exactly that.

     Blocked requests (Shields, an ad blocker, an extension) reject with a
     TypeError, which is the signal the old code had no way to see. */
  function postAttempt (url, body) {
    if (typeof fetch !== 'function') return Promise.resolve(false);

    var live = fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: body,
      keepalive: true
    }).then(function () { return true; }, function () { return false; });

    /* Capped, but never aborted. A slow request is left to finish under
       keepalive — cancelling it to meet the deadline would throw away a
       delivery that was about to succeed. Timing out only means "stop
       waiting", and the outbox entry survives to be retried. */
    var capped = new Promise(function (resolve) { setTimeout(function () { resolve(false); }, SEND_DEADLINE); });
    return Promise.race([live, capped]);
  }

  // Fallback, and explicitly not trusted: on Brave this returns true having
  // sent nothing. Fired for the browsers where it does work, its answer
  // logged for the console and then ignored.
  function beaconAttempt (url, body) {
    if (!navigator.sendBeacon) return false;
    try {
      return navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
    } catch (e) { return false; }
  }

  /* Last resort: the same fields as a GET, as an image load. A plain
     top-level GET to script.google.com is the request shape least likely to
     be filtered, and it needs no CORS at all.

     It only lands if the Apps Script implements doGet — see the note by
     CzardAccess.sheet(). Harmless if it does not: the response is not an
     image either way, so nothing here can tell success from failure, which
     is why this never clears the outbox. */
  function imgAttempt (url, payload) {
    try {
      var q = [];
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) {
          q.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(payload[k])));
        }
      }
      var src = url + (url.indexOf('?') === -1 ? '?' : '&') + q.join('&');
      if (src.length > 7000) return false;   // past what a URL can carry
      var img = new Image();
      img.referrerPolicy = 'no-referrer-when-downgrade';
      img.src = src;
      return true;
    } catch (e) { return false; }
  }

  /* ──────────────────────────────────────────── the same-origin rescue ──

     ERR_BLOCKED_BY_CLIENT against script.google.com is not a transport
     problem, and no amount of retrying solves it. Brave's Shields — and
     EasyPrivacy, which uBlock and most other blockers ship — filter Apps
     Script /macros/ endpoints outright, because that is exactly the shape
     of a third-party data-collection sink. Every request this file can make
     to that host is blocked: fetch, beacon, image, first attempt and fifth
     alike. The outbox will carry that lead to its retry limit and lose it.

     What is never blocked is the shop's own origin. So a lead that cannot
     reach Google is posted to Shopify's contact endpoint instead, which is
     first-party, needs no CORS, and no blocklist touches. It arrives as a
     contact-form email to the store address rather than a sheet row — not
     where it was meant to go, but recoverable, which is the whole point.

     This sends a store email per blocked lead. To turn it off, set
     data-czard-rescue="off" on #golden-fade-wrapper, or window
     .CZARD_ACCESS_RESCUE = false before this file loads — and accept that
     leads from shielded browsers are then simply lost.

     The proper fix is to stop being third-party at all: a Shopify App
     Proxy at /apps/czard-access forwarding to the Apps Script makes the
     request same-origin, and the sheet works everywhere. Until that exists,
     this is the floor. */

  function rescueEnabled () {
    if (window.CZARD_ACCESS_RESCUE === false) return false;
    var scene = document.getElementById('golden-fade-wrapper');
    return !(scene && scene.getAttribute('data-czard-rescue') === 'off');
  }

  function rescue (payload) {
    if (!rescueEnabled()) return false;

    var body = [
      'form_type=contact',
      'utf8=' + encodeURIComponent('✓'),
      'contact[name]=' + encodeURIComponent(payload.name || ''),
      'contact[email]=' + encodeURIComponent(payload.email || ''),
      'contact[phone]=' + encodeURIComponent(payload.phone || ''),
      'contact[body]=' + encodeURIComponent(
        'CZARD Request Access — this lead could not reach the Google Sheet, ' +
        'because the visitor\'s browser blocked the request.\n\n' +
        'Name: ' + payload.name + '\n' +
        'Email: ' + payload.email + '\n' +
        'WhatsApp: ' + payload.phone + '\n' +
        'Opt-in: ' + (payload.user_opt_in ? 'yes' : 'no') + '\n' +
        'Submitted: ' + payload.timestampIST + ' IST (' + payload.timestamp + ')\n' +
        'Page: ' + payload.page + '\n' +
        'User id: ' + payload.user_id + '\n' +
        'Row id: ' + payload.id
      )
    ].join('&');

    try {
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body,
        keepalive: true
        // same-origin, so the response is readable — but it is a redirect to
        // a themed page and says nothing useful, so it is not inspected
      }).then(function () {
        console.info('[czard-access] the lead was emailed to the store via ' +
                     'Shopify\'s contact form, because the Google Sheet was blocked.');
      }, function (err) {
        console.warn('[czard-access] the same-origin rescue failed too. This lead ' +
                     'exists only in this browser\'s outbox.', err);
      });
      return true;
    } catch (e) { return false; }
  }

  function outboxMark (id, patch) {
    var list = outbox();
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) { for (var k in patch) list[i][k] = patch[k]; }
    }
    outboxSave(list);
  }

  /* One outbox entry out the door. Resolves true only when the POST
     confirmed; anything else leaves the entry in place for the next load. */
  function deliver (entry) {
    var url = sheetUrl();
    if (!url) return Promise.resolve(false);

    var body = JSON.stringify(entry.payload);

    return postAttempt(url, body).then(function (ok) {
      if (ok) {
        outboxDrop(entry.id);
        ledgerMark(entry.payload.user_id, entry.id, true);
        console.info('[czard-access] row delivered to Google Sheet →', url);
        return true;
      }

      var beaconed = beaconAttempt(url, body);
      imgAttempt(url, entry.payload);
      console.warn(
        '[czard-access] the Google Sheet POST did not confirm — a browser shield, ' +
        'an ad blocker or the network stopped it. Fallbacks fired (sendBeacon: ' +
        (beaconed ? 'accepted, though Brave returns true without sending' : 'unavailable') +
        ', image GET: sent), none of which can be verified from here. The lead is ' +
        'held in the outbox and retried on the next page load.', url
      );

      // Once per lead, on the first failure rather than after the retries:
      // when the host is blocklisted every retry fails too, and waiting five
      // page loads to find that out only risks losing the visitor entirely.
      if (!entry.rescued) {
        entry.rescued = true;
        outboxMark(entry.id, { rescued: true });
        rescue(entry.payload);
      }
      return false;
    });
  }

  // Anything a previous visit could not confirm. Runs on load and on
  // pageshow, so the retry rides the navigation the submit itself triggers.
  var flushing = false;
  function flush () {
    if (flushing) return Promise.resolve();
    var list = outbox();
    if (!list.length || !sheetUrl()) return Promise.resolve();

    flushing = true;
    var now = Date.now(), live = [], chain = Promise.resolve();

    var keep = [];
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      // dropped: malformed, stale, or out of retries
      if (!e || !e.id || (now - (e.at || 0)) > OUTBOX_TTL || (e.attempts || 0) >= MAX_ATTEMPTS) continue;
      /* An entry queued before the payload changed shape still carries
         dedupeKey and consent. It would otherwise be POSTed in the old
         format days after the Apps Script was updated to the new one, and
         land as a row of blanks — the one failure mode of this change that
         costs a real lead rather than a duplicate. Upgraded in place. */
      if (e.payload && e.payload.user_id === undefined) {
        e.payload.user_id = userId(e.payload.phone);
        e.payload.user_opt_in = !!e.payload.consent;
        delete e.payload.dedupeKey;
        delete e.payload.consent;
      }
      keep.push(e);
      if ((now - (e.lastTry || 0)) < RETRY_GAP) continue;   // held, not retried yet
      e.attempts = (e.attempts || 0) + 1;
      e.lastTry = now;
      live.push(e);
    }
    outboxSave(keep);   // also drops whatever expired or ran out of attempts
    if (!live.length) { flushing = false; return Promise.resolve(); }

    live.forEach(function (e) {
      chain = chain.then(function () { return deliver(e); });
    });

    return chain.then(function () { flushing = false; }, function () { flushing = false; });
  }

  /* ─────────────────────────────────────────────────────────── timestamps

     new Date().toISOString() is UTC, and it says so with a trailing Z —
     which Sheets does not read as a date at all. It lands as text, sorts as
     text, and shows a time five and a half hours behind the shop. Nothing
     was wrong with the clock; the row was simply never in IST.

     Three fields now go out, because they answer different questions:

       timestamp     ISO 8601 UTC. Unambiguous, machine-sortable, the one
                     to keep if the row is ever exported or compared.
       timestampIST  'DD/MM/YYYY HH:MM:SS' in Asia/Kolkata. What a person
                     reading the sheet actually wants. Point the sheet's
                     visible date column at this one.
       timezone      where the visitor was, which is the only way to tell a
                     3am submission in India from an afternoon one abroad.

     The better fix still belongs in the Apps Script, and is one line:
     Utilities.formatDate(new Date(), 'Asia/Kolkata', 'dd/MM/yyyy HH:mm:ss')
     — a server clock cannot be wrong, and a visitor's can be, wildly. Use
     these when the sheet must record when the visitor submitted; use the
     server's when it must record when the row was written. */
  function stamps () {
    var d = new Date();
    var out = { timestamp: d.toISOString(), timestampIST: '', timezone: '' };

    try {
      // en-GB for 24-hour, day-first — no reading of 06/08 as August the 6th
      var f = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
      out.timestampIST = f.format(d).replace(',', '');
      out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      // Intl missing or the zone unrecognised: fall back to the offset by
      // hand, so the field is never empty and never silently wrong
      var ist = new Date(d.getTime() + (5.5 * 36e5));
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      out.timestampIST = pad(ist.getUTCDate()) + '/' + pad(ist.getUTCMonth() + 1) + '/' +
                         ist.getUTCFullYear() + ' ' + pad(ist.getUTCHours()) + ':' +
                         pad(ist.getUTCMinutes()) + ':' + pad(ist.getUTCSeconds());
      /* The zone was left empty on this path, which reads in the sheet as
         "the visitor had no timezone" rather than "this browser could not
         report one". The offset is always available, so it is written in
         the form Sheets shows for an unknown zone. */
      try {
        var off = -d.getTimezoneOffset();
        out.timezone = 'UTC' + (off < 0 ? '-' : '+') + pad(Math.floor(Math.abs(off) / 60)) +
                       ':' + pad(Math.abs(off) % 60);
      } catch (e2) {}
    }
    return out;
  }

  function toSheet (p) {
    var url = sheetUrl();
    if (!url) {
      // Silence here was the problem: nothing was written and nothing said so.
      var scene = document.getElementById('golden-fade-wrapper');
      console.warn(
        '[czard-access] no Google Sheet URL, so no row was written. ' +
        (scene && scene.hasAttribute('data-czard-sheet')
          ? 'The theme setting is empty — paste the Apps Script /exec URL into ' +
            'Home → Golden Fade Experience → Google Sheet.'
          : 'sections/hero-houseofczard.liquid does not carry data-czard-sheet at ' +
            'all — that file is not deployed, so the setting cannot reach this script.')
      );
      return Promise.resolve(false);
    }

    /* Written already? Then say nothing more. This is the guard that holds
       across page loads and across visits — the in-memory one in submit()
       only covers a single burst of clicks. */
    var key = userId(p.phone);
    var seen = alreadyWritten(key);
    if (seen) {
      console.info('[czard-access] ' + key + ' is already in the sheet (' + seen +
                   '), so no second row was written.');
      return Promise.resolve(true);
    }

    var t = stamps();
    var entry = {
      id: uid(),
      at: Date.now(),
      attempts: 1,
      lastTry: Date.now(),
      payload: {
        /* The person. The WhatsApp number in E.164, and the field the sheet
           should dedupe rows on — the same visitor arriving again from
           another browser or another device is the one repeat nothing in
           this script can see for itself. */
        user_id: key,
        // Same id on every retry, so the Apps Script can drop a repeat rather
        // than write the lead twice. Not the person — one send attempt. See
        // the dedupe note on CzardAccess.sheet().
        id: '',
        timestamp: t.timestamp,
        timestampIST: t.timestampIST,
        timezone: t.timezone,
        name: p.name,
        email: p.email,
        // repeated deliberately: user_id is the key and this is the value.
        // A sheet keyed on a column it also reads as data is a sheet that
        // breaks the day the key changes.
        phone: p.phone,
        // the marketing-consent audit trail; the sheet is the durable
        // record. A real boolean, so it lands in Sheets as TRUE rather than
        // the text "TRUE" — sortable and filterable as a checkbox column.
        user_opt_in: !!p.consent,
        page: location.href,
        referrer: document.referrer || '',
        userAgent: navigator.userAgent,
        // which build of this file wrote the row. When a browser is serving
        // a cached copy — which is what a stale VERSION in the sheet means —
        // that is worth knowing from the row itself rather than guessing.
        v: VERSION
      }
    };
    entry.payload.id = entry.id;

    // Persist BEFORE sending. If the send is blocked, or the tab is closed
    // mid-flight, the lead is still on disk to retry — which is the whole
    // reason a silent block is survivable now.
    outboxAdd(entry);
    // Claimed, not yet confirmed: enough to stop a second write while this
    // one is in the air, and it upgrades to confirmed when deliver() lands.
    ledgerMark(key, entry.id, false);

    return deliver(entry);
  }

  /* ====================================================== 4. VALIDATION */

  // WebEngage rejects anything that is not E.164, and a rejected we_phone
  // silently costs the WhatsApp channel — so this is strict. A number typed
  // with its own + wins; otherwise the dial code beside the field is joined
  // on, after stripping a national trunk zero.
  function toE164 (dial, raw) {
    var typed = String(raw || '').trim();
    var digits = typed.replace(/\D/g, '');
    if (!digits) return '';
    if (typed.charAt(0) === '+') return '+' + digits;
    digits = digits.replace(/^0+/, '');
    var code = String(dial || '').replace(/\D/g, '');
    return code ? '+' + code + digits : '+' + digits;
  }

  var okPhone = function (v) { return /^\+[1-9]\d{7,14}$/.test(v); };
  // Loose on purpose — a stricter pattern rejects real addresses.
  var okEmail = function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); };

  /* Every rule in one place, each returning the message to show or ''.

     Written this way because submit() and the blur handlers must agree
     exactly: a field that turns red as the visitor leaves it, then passes
     on submit — or the reverse — reads as the form being broken. One
     function per field, called from both, cannot drift.

     The messages say what to do, not what is wrong: "include the country
     code" is actionable, "invalid format" is not. */

  function checkName () {
    var v = (fName.value || '').trim();
    if (!v) return 'Please enter your name.';
    if (v.length < 2) return 'Please enter your full name.';
    return '';
  }

  function checkEmail () {
    var v = (fEmail.value || '').trim();
    if (!v) return 'Please enter your email address.';
    // The three things people actually mistype, named specifically. A
    // generic "invalid email" leaves them staring at an address that looks
    // right to them.
    if (v.indexOf('@') === -1) return 'Please include an @ in the email address.';
    if (v.indexOf(' ') !== -1) return 'An email address cannot contain spaces.';
    if (!/\.[^\s@.]{2,}$/.test(v)) return 'Please include a domain ending, like .com';
    if (!okEmail(v)) return 'Please enter a valid email address.';
    return '';
  }

  /* The dial code and the number are two fields but one value, so they are
     checked together and the message points at whichever half is wrong. */
  function checkPhone () {
    var raw = (fPhone.value || '').trim();
    var typedDigits = raw.replace(/\D/g, '');
    if (!typedDigits) return 'Please enter your WhatsApp number.';

    var dial = (fDial.value || '').trim();
    if (raw.charAt(0) !== '+' && !dial.replace(/\D/g, '')) {
      return 'Please enter your country code, like +91.';
    }

    var e164 = toE164(dial, raw);
    // Length before pattern: "too short" is a far more useful thing to be
    // told than "invalid", and it is what a missing digit actually is.
    var n = e164.replace(/\D/g, '').length;
    if (n < 8) return 'That number looks too short. Please check it.';
    if (n > 15) return 'That number looks too long. Please check it.';
    if (!okPhone(e164)) return 'Please enter a valid WhatsApp number, including the country code.';
    return '';
  }

  function checkConsent () {
    return fConsent.checked ? '' : 'Please agree to receive updates before continuing.';
  }

  /* ========================================================== 4b. STYLES

     Injected rather than loaded. A stylesheet that fails to upload used to
     leave the panel unpositioned, rendering it behind the home page's fixed
     WebGL canvas — the form opened correctly and the visitor saw nothing
     happen. No backdrop-filter: blurring a live 3D canvas every frame is
     what made this crawl to open. */
  var CSS =
  '.cza-veil{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;' +
  'padding:1rem;background:rgba(10,8,6,.82);opacity:0;transition:opacity .2s ease-out;overflow-y:auto;' +
  /* the scroll chain used to reach the page behind: scrolling past the end
     of a tall form on a phone dragged the locked body underneath it */
  'overscroll-behavior:contain;' +
  'font-family:Jost,system-ui,-apple-system,sans-serif}' +
  '.cza-veil[hidden]{display:none}.cza-veil.is-on{opacity:1}' +
  '.cza-box{position:relative;width:min(100%,30rem);margin:auto;padding:2.25rem 1.75rem;background:#fdf6ea;' +
  'color:#2d1e0e;border:1px solid rgba(45,30,14,.18);box-shadow:0 30px 80px rgba(10,7,3,.45);' +
  'transform:translateY(12px);transition:transform .26s cubic-bezier(.16,1,.3,1)}' +
  '.cza-veil.is-on .cza-box{transform:none}' +
  '.cza-x{position:absolute;top:.6rem;right:.6rem;width:2.2rem;height:2.2rem;border:0;background:none;' +
  'color:rgba(45,30,14,.6);font-size:1rem;line-height:1;cursor:pointer;border-radius:50%}' +
  '.cza-x:hover{background:rgba(45,30,14,.08);color:#2d1e0e}' +
  '.cza-eyebrow{margin:0 0 .6rem;font-size:.6rem;font-weight:600;letter-spacing:.28em;text-transform:uppercase;color:#9c6b3f}' +
  '.cza-title{margin:0;font-family:var(--heading,Georgia,serif);font-weight:400;font-size:1.9rem;line-height:1.1;' +
  'letter-spacing:.02em;text-transform:uppercase;color:#2d1e0e}' +
  '.cza-sub{margin:.8rem 0 0;font-size:.85rem;line-height:1.6;color:rgba(45,30,14,.66)}' +
  '.cza-field{display:block;margin:1.4rem 0 0}' +
  '.cza-label{display:block;margin-bottom:.35rem;font-size:.62rem;font-weight:600;letter-spacing:.18em;' +
  'text-transform:uppercase;color:rgba(45,30,14,.66)}' +
  /* 16px is the floor below which iOS Safari zooms the viewport on focus */
  '.cza-in{display:block;width:100%;padding:.75rem .85rem;border:1px solid rgba(45,30,14,.18);border-radius:0;' +
  'background:rgba(255,255,255,.72);color:#2d1e0e;font-family:inherit;font-size:16px;-webkit-appearance:none;appearance:none}' +
  '.cza-in::placeholder{color:rgba(45,30,14,.38)}' +
  '.cza-in:focus{outline:none;border-color:#9c6b3f;background:#fff}' +
  /* Keyboard focus has to be visible. outline:none above removes the
     browser's own ring, and the border tint alone is not enough to show
     which field is focused when tabbing through. */
  '.cza-in:focus-visible{outline:2px solid #9c6b3f;outline-offset:2px}' +
  '.cza-go:focus-visible,.cza-x:focus-visible{outline:2px solid #9c6b3f;outline-offset:3px}' +
  '.cza-row{display:flex;gap:.5rem}.cza-dial{width:4.5rem;flex:0 0 auto;text-align:center}' +
  '.cza-err{margin:1rem 0 0;font-size:.78rem;line-height:1.5;color:#a33327}.cza-err[hidden]{display:none}' +
  /* Per-field state. The border carries it as well as the text: colour
     alone is not a message anyone required to read it can rely on, and the
     text below the field is what actually says so. */
  '.cza-in.is-bad{border-color:#a33327}' +
  '.cza-in.is-bad:focus{border-color:#a33327}' +
  '.cza-fe{display:block;margin:.4rem 0 0;font-size:.7rem;line-height:1.45;color:#a33327}' +
  '.cza-fe[hidden]{display:none}' +
  '.cza-go{display:block;width:100%;margin-top:1.5rem;padding:.95rem 1.5rem;border:0;border-radius:999px;' +
  'background:#3b2914;color:#fff6e6;font-family:inherit;font-size:.72rem;font-weight:600;letter-spacing:.16em;' +
  'text-transform:uppercase;cursor:pointer}' +
  '.cza-go:hover{background:#4b3418}.cza-go:disabled{opacity:.6;cursor:default}' +
  '.cza-note{margin:1rem 0 0;font-size:.68rem;line-height:1.55;color:rgba(45,30,14,.55)}' +
  /* align-items:flex-start so a wrapped consent line keeps the box beside
     its first line rather than centred against the whole paragraph */
  '.cza-consent{display:flex;align-items:flex-start;gap:.55rem;margin-top:1.3rem;' +
  'font-size:.72rem;line-height:1.5;color:rgba(45,30,14,.75);cursor:pointer}' +
  '.cza-consent input{flex:0 0 auto;width:1rem;height:1rem;margin:.1rem 0 0;accent-color:#3b2914;cursor:pointer}' +
  '.cza-done{text-align:center}.cza-done[hidden]{display:none}' +
  /* the home scene hides the real cursor and draws its own ring; neither
     belongs over a form */
  'html.cza-open,html.cza-open body{overflow:hidden}' +
  'html.cza-open body{cursor:auto}html.cza-open #cursor{display:none}' +
  /* WebEngage's own in-app notification modal. It renders a full-viewport
     iframe carrying a second, WebEngage-authored form, which appears behind
     this one — two forms asking for the same details.

     Suppressed here because the iframe is injected at runtime and cannot be
     reached from Liquid. Note this hides every on-site WebEngage
     notification, not only the modal: campaigns of any layout render into
     the same container. The proper fix is to pause the campaign in the
     WebEngage dashboard; this only stops it being seen. */
   '#webengage-notification-container,' +
   '#webklipper-publisher-widget-container-notification-frame{display:none!important}' +
  /* The old stylesheet honoured this and the rewrite dropped it. The panel
     slides and fades on open, which is exactly the motion someone with a
     vestibular disorder has asked the OS not to show them. */
  '@media (prefers-reduced-motion:reduce){' +
    '.cza-veil,.cza-box{transition:none}' +
    '.cza-box{transform:none}}';

  var styleEl = document.createElement('style');
  styleEl.textContent = CSS;
  document.head.appendChild(styleEl);

  /* ============================================================ 5. MODAL

     Built here. Any modal left over from the old Liquid snippet is removed
     first, so there is exactly one code path rather than two. */

  /* Copy taken from the WebEngage campaign this replaces, so the two are
     interchangeable and nothing the visitor was promised changes. */
  var COPY = {
    eyebrow: 'The House of CZARD',
    title: 'Request Access',
    sub: 'Leave your details and the collection opens to you — pricing, provenance and the full record of every piece.',
    name: 'Full Name *', email: 'Email Address *', phone: 'WhatsApp Number *',
    phName: 'Enter Full name', phEmail: 'name@example.com', phPhone: 'Enter Phone Number',
    dial: '+91',
    consent: 'I agree to receive updates and exclusive offers from CZARD via email and WhatsApp.',
    submit: 'Continue',
    doneTitle: 'Thank You',
    doneBody: 'Thank you for submitting your request. You will be redirected to our collections page shortly.',
    doneClose: 'Close'
  };

  var old = document.querySelector('[data-czard-access]');
  if (old && old.parentNode) old.parentNode.removeChild(old);

  var veil = document.createElement('div');
  veil.className = 'cza-veil';
  veil.hidden = true;
  veil.setAttribute('role', 'dialog');
  veil.setAttribute('aria-modal', 'true');
  // A dialog with no accessible name is announced as just "dialog". The
  // heading is the name, so it is pointed at rather than duplicated.
  veil.setAttribute('aria-labelledby', 'cza-title');
  /* ───────────────────────────── the WebEngage field identity ───────────
     Every control below carries the same identity as the corresponding
     control in the WebEngage campaign form this modal replaces:

       name          text      name             we_wk-text-name
       email         email     email            we_wk-email-email
       WhatsApp      tel       label-6          we_wk-tel-label-6
       consent       checkbox  privacy          we_wk-checkbox-privacy
       submit        submit    submit           we_wk-submit-submit

     That mapping is what WebEngage's form capture reads. Without it the
     modal was a set of anonymous inputs inside a plain <div> — nothing
     named, nothing to map onto, so the capture had nothing to file the
     submission under and the WebEngage side saw fields that did not match
     the campaign's.

     Two things from the reference markup are deliberately NOT copied:

       action / enctype   the reference posts to a placeholder endpoint.
                          This form is handled entirely in JS and has no
                          action at all, so a stray native submit reloads
                          the page at worst rather than posting anywhere.
       data-we_wk_element_id / data-we_wk_navigation
                          per-campaign instance ids. Copied verbatim they
                          would invite the SDK to drive these nodes as if
                          they were its own popup — its state machine, its
                          close button, its P0→P1 transition. The role
                          attributes above are the part that describes the
                          field; the ids describe one campaign.

     novalidate, because the messages in section 4 are the only ones the
     visitor should see. required and pattern are still declared — they are
     part of the field's shape and the capture reads them — but the browser
     is told not to act on them, so a native bubble can never appear in
     place of "Please include a domain ending, like .com". */
  veil.innerHTML =
    '<div class="cza-box">' +
      '<button class="cza-x" type="button" data-x name="close" aria-label="Close">&#10005;</button>' +
      '<div data-form>' +
        '<p class="cza-eyebrow">' + COPY.eyebrow + '</p>' +
        '<h2 class="cza-title" id="cza-title">' + COPY.title + '</h2>' +
        '<p class="cza-sub">' + COPY.sub + '</p>' +
        '<form data-form-el novalidate accept-charset="UTF-8" autocomplete="on">' +
        /* Each field carries its own message element, and the input points
           at it with aria-describedby so a screen reader reads the reason
           along with the field rather than leaving it to be found. A span:
           a <p> is not valid inside a <label>.

           The label wraps its input, so the association is implicit and no
           `for` is needed — the reference uses `for` only because its
           labels are siblings. The one exception is the WhatsApp label
           below, which really is a sibling and really does need it. */
        '<label class="cza-field"><span class="cza-label">' + COPY.name + '</span>' +
          '<input class="cza-in" data-n id="cza-name" name="name" type="text" required autocomplete="name" placeholder="' + COPY.phName + '" aria-describedby="cza-fe-n" data-we_wk__form_control="we_wk__form_control" data-we_wk_element="we_wk-text-name">' +
          '<span class="cza-fe" id="cza-fe-n" data-fe-n hidden></span></label>' +
        '<label class="cza-field"><span class="cza-label">' + COPY.email + '</span>' +
          '<input class="cza-in" data-e id="cza-email" name="email" type="email" required autocomplete="email" inputmode="email" placeholder="' + COPY.phEmail + '" aria-describedby="cza-fe-e" data-we_wk__form_control="we_wk__form_control" data-we_wk_element="we_wk-email-email">' +
          '<span class="cza-fe" id="cza-fe-e" data-fe-e hidden></span></label>' +
        '<div class="cza-field"><label class="cza-label" for="cza-phone">' + COPY.phone + '</label>' +
          '<div class="cza-row">' +
            /* The dial box has no name, on purpose. It exists for the
               visitor, not for the capture: the reference form has one tel
               field carrying a whole E.164 number, so a second named field
               holding "+91" would map onto nothing and read as a stray. */
            '<input class="cza-in cza-dial" data-d id="cza-dial" type="text" inputmode="tel" value="' + COPY.dial + '" aria-label="Country code">' +
            '<input class="cza-in" data-p id="cza-phone" type="tel" required inputmode="tel" autocomplete="tel-national" placeholder="' + COPY.phPhone + '" aria-describedby="cza-fe-p" data-we_wk__form_control="we_wk__form_control">' +
          '</div>' +
          /* ...and this is the field the capture actually reads: the two
             boxes above joined into the single E.164 value the reference
             form's one tel input holds, kept in sync as either is typed.

             It is the mirror rather than the visible box that carries
             name="label-6" because the visible box holds a national number
             — "9876543210" — which is not what was submitted and does not
             satisfy the pattern the reference declares. Mapping the visible
             box would file a number missing its country code, which is
             precisely the value WhatsApp cannot reach. */
          '<input data-p-e164 name="label-6" type="tel" hidden tabindex="-1" aria-hidden="true" pattern="^\\+[1-9]\\d{1,14}$" title="Use international format." data-we_wk__form_control="we_wk__form_control" data-we_wk_element="we_wk-tel-label-6">' +
          '<span class="cza-fe" id="cza-fe-p" data-fe-p hidden></span></div>' +
        /* Required, and the reason the opt-ins are set again: this is
           explicit consent, which is what WhatsApp's policy demands before a
           number may be marked reachable. Unchecked, nothing is sent. */
        '<label class="cza-consent"><input type="checkbox" data-c id="cza-consent" name="privacy" required data-we_wk__form_control="we_wk__form_control" data-we_wk_element="we_wk-checkbox-privacy">' +
          '<span>' + COPY.consent + '</span></label>' +
        '<p class="cza-err" data-err hidden role="alert"></p>' +
        /* type="submit" now, where it was type="button". The old comment
           said a button could not be carried away by "any form anywhere",
           which was true and is now unnecessary: it sits inside its own
           form, whose submit handler is the single entry point below and
           preventDefaults every time. That real submit event is also the
           thing WebEngage's capture listens for — with type="button" no
           such event ever fired, so the mapping had nothing to fire on. */
        '<button class="cza-go" type="submit" data-go name="submit" value="' + COPY.submit + '" data-we_wk__form_control="we_wk__form_control" data-we_wk_element="we_wk-submit-submit">' + COPY.submit + '</button>' +
        '</form>' +
      '</div>' +
      '<div class="cza-done" data-done hidden>' +
        /* tabindex="-1" so focus can be moved here once the form is
           replaced. Without it focus stays on a button that no longer
           exists, which drops a screen reader back to the top of the page
           with no idea the submission succeeded. */
        '<h2 class="cza-title" id="cza-done-title" data-done-title tabindex="-1">' + COPY.doneTitle + '</h2>' +
        '<p class="cza-sub">' + COPY.doneBody + '</p>' +
        // the visitor may not want to wait out the redirect
        '<button class="cza-go" type="button" data-done-go>' + COPY.doneClose + '</button>' +
      '</div>' +
    '</div>';

  function mount () {
    if (!veil.parentNode && document.body) document.body.appendChild(veil);
  }
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);

  var $ = function (sel) { return veil.querySelector(sel); };
  var fName = $('[data-n]'), fEmail = $('[data-e]'), fDial = $('[data-d]'), fPhone = $('[data-p]');
  var fConsent = $('[data-c]'), fPhoneOut = $('[data-p-e164]');
  var elErr = $('[data-err]'), elGo = $('[data-go]'), elForm = $('[data-form]'), elDone = $('[data-done]');
  var elFormEl = $('[data-form-el]');
  var msgName = $('[data-fe-n]'), msgEmail = $('[data-fe-e]'), msgPhone = $('[data-fe-p]');

  /* The mapped copy of the number, refreshed on every keystroke in either
     half of the phone row.

     Live rather than written once at submit: WebEngage's capture reads the
     form from its own listener on the submit event, and there is no saying
     whether that listener runs before or after this file's. A value only
     assembled inside submit() would be a keystroke behind if it lost that
     race — and "a keystroke behind" on a phone number is a wrong number,
     not a stale one. Kept current at all times, the ordering stops
     mattering. */
  function syncPhoneMirror () {
    if (!fPhoneOut) return;
    var v = toE164((fDial.value || '').trim(), fPhone.value);
    if (fPhoneOut.value !== v) fPhoneOut.value = v;
  }

  var isOpen = false, heldHref = null;
  // whatever had focus before the form took it, so closing puts the visitor
  // back where they were rather than at the top of the document
  var lastFocus = null;

  /* ================================================== 5b. LIVE VALIDATION

     Three rules, and they are about when to speak rather than what to say:

       on blur    — check the field they have just left. Correcting a
                    mistake immediately is far cheaper than being handed a
                    list of them at the end.
       on input   — only ever CLEAR a message, never raise one. Validating
                    as each character lands means telling someone their
                    email is wrong while they are still typing the @, which
                    is nagging, not help.
       on submit  — check everything, mark every bad field, focus the first.

     The field a visitor has not touched yet stays silent until submit. */

  var FIELDS = [
    { el: fName,  msg: msgName,  check: checkName },
    { el: fEmail, msg: msgEmail, check: checkEmail },
    { el: fPhone, msg: msgPhone, check: checkPhone }
  ];

  function setFieldError (f, text) {
    if (text) {
      f.msg.textContent = text;
      f.msg.hidden = false;
      f.el.classList.add('is-bad');
      f.el.setAttribute('aria-invalid', 'true');
    } else {
      f.msg.hidden = true;
      f.msg.textContent = '';
      f.el.classList.remove('is-bad');
      f.el.removeAttribute('aria-invalid');
    }
  }

  function clearAllErrors () {
    for (var i = 0; i < FIELDS.length; i++) setFieldError(FIELDS[i], '');
    elErr.hidden = true;
    elErr.textContent = '';
  }

  for (var fi = 0; fi < FIELDS.length; fi++) {
    (function (f) {
      f.el.addEventListener('blur', function () {
        // an empty field they merely tabbed through is not yet a mistake
        if (!(f.el.value || '').trim()) { setFieldError(f, ''); return; }
        setFieldError(f, f.check());
      });
      f.el.addEventListener('input', function () {
        if (f.msg.hidden) return;           // nothing showing, nothing to clear
        if (!f.check()) setFieldError(f, ''); // fixed — say so at once
      });
    })(FIELDS[fi]);
  }

  /* The dial code re-checks the number rather than itself: the two are one
     value, and changing +91 to +44 can make a valid number invalid without
     the number field being touched at all. */
  fDial.addEventListener('input', function () {
    // keep it to a + and digits, so a stray letter cannot silently break
    // the E.164 assembly further down
    var cleaned = fDial.value.replace(/[^\d+]/g, '');
    if (cleaned && cleaned.charAt(0) !== '+') cleaned = '+' + cleaned.replace(/\+/g, '');
    else cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
    if (cleaned !== fDial.value) fDial.value = cleaned;
    syncPhoneMirror();
    if (!msgPhone.hidden && !checkPhone()) setFieldError(FIELDS[2], '');
  });

  /* Digits and the separators people naturally type. Letters are dropped as
     they are typed rather than rejected afterwards — nothing about a phone
     number needs them, and toE164 would strip them anyway, so the visitor
     may as well see exactly what is being sent. */
  fPhone.addEventListener('input', function () {
    var cleaned = fPhone.value.replace(/[^\d\s\-()+]/g, '');
    if (cleaned !== fPhone.value) {
      // preserve the caret, which naive reassignment sends to the end
      var at = fPhone.selectionStart, drop = fPhone.value.length - cleaned.length;
      fPhone.value = cleaned;
      try { fPhone.setSelectionRange(at - drop, at - drop); } catch (e) {}
    }
    syncPhoneMirror();
  });

  // and once at boot, so the mapped field is never empty-by-default while
  // the dial box already holds a country code
  syncPhoneMirror();

  // Trimmed on the way out of the field: a trailing space pasted along with
  // an address is the most common "valid email rejected" complaint there is.
  fEmail.addEventListener('blur', function () {
    var t = (fEmail.value || '').trim();
    if (t !== fEmail.value) fEmail.value = t;
  }, true);

  fConsent.addEventListener('change', function () {
    if (fConsent.checked && !elErr.hidden) { elErr.hidden = true; elErr.textContent = ''; }
  });

  function lockScroll (on) {
    document.documentElement.classList.toggle('cza-open', on);
    var l = window.lenisScroll && window.lenisScroll.lenis;
    if (l) { try { on ? l.stop() : l.start(); } catch (e) {} }
  }

  /* The destination, whatever shape the caller passed it in.

     Two call sites reach open() and they did not agree. The interceptor in
     section 6 passes (href, source). The home scene's canvas handlers pass a
     single { href, source } object — and those are the routes out of the
     finale gallery, the 360 spin and a chapter's watch image, none of which
     are anchors, so none of which the interceptor can see. That object was
     stored as heldHref verbatim and handed to go() after a successful
     submit, where it is not a URL at all: the visitor filled the form in,
     was thanked, and stayed exactly where they were.

     Normalised here rather than only at the call site, so neither file has to
     be the current one for the redirect to work. */
  function destination (href) {
    if (href && typeof href === 'object') return destination(href.href || href.url);
    return (typeof href === 'string' && href && href !== '#') ? href : null;
  }

  function open (href, source) {
    if (href && typeof href === 'object' && !source) source = href.source;
    href = destination(href);
    mount();
    if (isOpen) return;
    if (granted()) { go(href); return; }
    heldHref = href;
    isOpen = true;
    lastFocus = document.activeElement;
    clearAllErrors();   // a reopened form starts clean, not mid-argument
    // A reopened form shows the form again, not the thank-you screen it was
    // left on — reachable when a submit finishes with nowhere to navigate.
    elForm.hidden = false;
    elDone.hidden = true;
    veil.setAttribute('aria-labelledby', 'cza-title');
    // ...and the submit lock with it, or the reopened form cannot be sent
    submitting = false;
    elGo.disabled = false;
    veil.hidden = false;
    requestAnimationFrame(function () { veil.classList.add('is-on'); });
    lockScroll(true);
    try { fName.focus({ preventScroll: true }); } catch (e) {}
    track('Request Access Opened', { Source: source || 'Unknown', 'Page URL': location.href });
  }

  function close () {
    if (!isOpen) return;
    isOpen = false;
    veil.classList.remove('is-on');
    lockScroll(false);

    /* Focus goes back to the link that opened this. Left alone it stays on
       a button inside a panel that is about to be hidden, and a hidden
       element with focus drops the caret to the top of the document — so a
       keyboard visitor who closes the form has to tab all the way back
       through the page to reach the link they were on. */
    var back = lastFocus;
    lastFocus = null;
    if (back && back.focus && document.contains(back)) {
      try { back.focus({ preventScroll: true }); } catch (e) {}
    }

    setTimeout(function () { if (!isOpen) veil.hidden = true; }, 260);
  }

  // Always the same tab. If framed — the theme editor preview does this —
  // navigate the top window so the editor cannot intercept it into a new one.
  function go (raw) {
    var href = destination(raw);
    /* Silent when there was genuinely nowhere to go — the form can be opened
       with no destination at all. Loud when something WAS passed and could
       not be read as a URL, which is the failure that used to navigate to
       the string "[object Object]" and say nothing about it. */
    if (!href) {
      if (raw) console.warn('[czard-access] the destination could not be read as a URL, ' +
                            'so the visitor was left where they are:', raw);
      return;
    }
    var framed; try { framed = window.top !== window.self; } catch (e) { framed = true; }
    if (framed) { try { window.top.location.href = href; return; } catch (e) {} }
    location.assign(href);
  }

  function fail (msg, field) {
    elErr.textContent = msg;
    elErr.hidden = false;
    if (field) field.focus();
    console.warn('[czard-access] rejected:', msg);
  }

  /* One submission per opening of the form, whatever fires it.

     elGo.disabled is not this guard and never was: it disables the button,
     while the Enter handler calls submit() directly and is unaffected by it.
     Holding Enter auto-repeats, and every repeat used to run the whole
     function — WebEngage events, sheet row and all. That is where duplicate
     rows came from in threes and fours, seconds apart, identical. */
  var submitting = false;

  function submit () {
    if (submitting) return;
    elErr.hidden = true;
    /* Belt and braces on the mapped number. The input listeners keep it
       current as the visitor types, but a browser autofilling the phone row
       does not always fire one — and a mirror that missed the autofill is
       the one case where the capture would read an empty field on a form
       the visitor had filled in completely. */
    syncPhoneMirror();

    /* Every field is checked, not just up to the first bad one. Being told
       about one mistake, fixing it, and being handed the next is three
       round trips where one will do — so all the messages go up together
       and the focus goes to the first field that needs it. */
    var firstBad = null, badCount = 0;
    for (var i = 0; i < FIELDS.length; i++) {
      var problem = FIELDS[i].check();
      setFieldError(FIELDS[i], problem);
      if (problem) { badCount++; if (!firstBad) firstBad = FIELDS[i].el; }
    }

    // The consent box has no field message of its own — it is a single
    // line, so the summary line beneath the form is where it belongs.
    var consentProblem = checkConsent();
    if (badCount) {
      elErr.textContent = badCount === 1
        ? 'Please correct the highlighted field.'
        : 'Please correct the ' + badCount + ' highlighted fields.';
      elErr.hidden = false;
      if (firstBad) firstBad.focus();
      console.warn('[czard-access] rejected: ' + badCount + ' field(s) need correcting');
      return;
    }
    if (consentProblem) return fail(consentProblem, fConsent);

    var name = (fName.value || '').trim();
    var email = (fEmail.value || '').trim();
    var phone = toE164((fDial.value || '').trim(), fPhone.value);

    // Set only once the form has passed validation, so a rejected attempt
    // can still be corrected and sent — this locks a real submission, not
    // the form itself.
    submitting = true;
    var profile = { name: name, email: email, phone: phone, consent: true };
    elGo.disabled = true;

    console.info('[czard-access] sending to WebEngage —', weReport());

    // Independent of WebEngage on purpose: the sheet is the durable record,
    // and an ad blocker that stops the WebEngage SDK does not touch this.
    var sent = toSheet(profile);

    identify(profile);
    track('Request Access Submitted', {
      Name: name, Email: email, WhatsApp: phone,
      Source: 'Website', 'Page URL': location.href
    });

    /* Sent alongside the one above, not instead of it. The name is written
       exactly as specified — WebEngage event names are case sensitive, so
       the underscores and the lower-case tail are load-bearing and must not
       be "tidied" into the spaced form the other events use. */
    track('Request_access_clicked_submitted', {
      Name: name, Email: email, WhatsApp: phone,
      Source: 'Website', 'Page URL': location.href
    });

    // Queued rather than sent means no usable SDK was on the page at all.
    if (queue.length) {
      console.warn(
        '[czard-access] ' + queue.length + ' WebEngage call(s) could not be sent — ' +
        'window.webengage is missing or unusable. They are queued and will go out ' +
        'if it appears within the page\'s lifetime, but a navigation will discard ' +
        'them. This lead may not reach WebEngage.', weReport()
      );
    }

    writeState(profile);   // dispatches the change event; the label loop reacts
    enforceLabels();

    elForm.hidden = true;
    elDone.hidden = false;

    /* The dialog's name changes with its contents, and focus follows it.
       Otherwise the submit button is hidden while it still holds focus,
       and the visitor is told nothing about a submission that worked. */
    veil.setAttribute('aria-labelledby', 'cza-done-title');
    var doneTitle = $('[data-done-title]');
    if (doneTitle) { try { doneTitle.focus({ preventScroll: true }); } catch (e) {} }

    var href = heldHref;

    /* The redirect waits for the row, rather than racing it.

       This used to be a flat 1200ms, which left the POST to survive the
       navigation on keepalive alone — the assumption that broke on Brave.
       Now the thank-you screen holds until the send settles, and deliver()
       is capped at SEND_DEADLINE so a hung request cannot strand anyone.
       In practice the send beats the 1200ms floor and nothing feels slower;
       when it does not, waiting a moment is the right trade against losing
       the lead. */
    var minShow = new Promise(function (r) { setTimeout(r, 1200); });
    Promise.all([minShow, sent]).then(function () {
      close();
      setTimeout(function () { go(href); }, 200);
    });
  }

  /* One entry point, and it is the form's own submit event.

     The button used to carry a click listener of its own. Now that it is a
     real submit button that would mean two runs per click — the listener,
     then the native submit — and a rejected attempt would print its
     messages and its console warning twice over. The submitting flag only
     covers an accepted one.

     Everything that can start a submission goes through the form instead,
     which is also the only event WebEngage's capture can see. */
  if (elFormEl) {
    elFormEl.addEventListener('submit', function (e) {
      e.preventDefault();   // nothing here ever navigates; JS owns the send
      submit();
    });
  }

  /* Ask the form to submit itself, rather than calling submit() directly:
     requestSubmit dispatches a real submit event, so the capture sees a
     keyboard submission exactly as it sees a click. Safari before 16 has no
     requestSubmit, where the direct call is the same submission minus the
     capture — the lead still reaches the sheet and WebEngage's own event. */
  function requestSubmit () {
    if (elFormEl && typeof elFormEl.requestSubmit === 'function') {
      try { elFormEl.requestSubmit(); return; } catch (e) {}
    }
    submit();
  }

  /* ---- everything that closes the panel, delegated from the veil ----

     The ✕ used to carry its own listener: `$('[data-x]').addEventListener
     ('click', close)`, bound once at boot to whatever that query returned at
     that moment. On the home page it stopped firing, while Escape and a
     backdrop click — which call this same close() — went on working. That
     rules out close() and the isOpen flag, and leaves only one thing: the
     listener was not on the ✕ that was on screen.

     A stale element reference is a class of causes rather than one — the node
     re-rendered, a second copy of this file mounting a second veil, something
     else adopting it — and none of them can be told apart from in here.
     Delegation sidesteps all of them: the veil is the element the backdrop
     test already runs on, so it demonstrably still receives the click, and
     matching with closest() does not care which button element the click
     landed on or when that element appeared.

     One listener for all three jobs, so they cannot disagree about what a
     click was — the backdrop test in particular has to run first, since it is
     the only one that must NOT match a click that came from inside the panel. */
  veil.addEventListener('click', function (e) {
    if (e.target === veil) { close(); return; }

    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('[data-x]')) { close(); return; }

    // "Close" on the thank-you screen: go now rather than wait out the redirect
    if (t.closest('[data-done-go]')) {
      var href = heldHref;
      close();
      go(href);
    }
  });
  /* Everything inside the panel that can take focus, in document order.
     Queried per keypress rather than cached: the form and the thank-you
     screen swap places, so a list taken once goes stale the moment a
     submission succeeds. */
  function focusables () {
    var all = veil.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      // offsetParent is null for anything inside the hidden half of the panel
      if (!el.disabled && el.offsetParent !== null) out.push(el);
    }
    return out;
  }

  veil.addEventListener('keydown', function (e) {
    /* aria-modal tells a screen reader the page behind is inert; it does
       nothing whatsoever to the Tab order. Without this, tabbing past the
       submit button walks into the page underneath — which on the home
       page is an invisible 3D scene, so focus simply disappears and the
       only way back is the mouse. Tab is wrapped inside the panel until
       the form is closed. */
    if (e.key === 'Tab') {
      var list = focusables();
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      // activeElement is the <body> when focus has already escaped
      if (!veil.contains(document.activeElement)) {
        e.preventDefault();
        try { first.focus(); } catch (err) {}
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        try { last.focus(); } catch (err) {}
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        try { first.focus(); } catch (err) {}
      }
      return;
    }

    // e.repeat: a held Enter key. The browser repeats it about 30 times a
    // second, and each one reached submit(). Ignored outright — nobody
    // means to submit a form by leaning on a key.
    if (e.repeat) return;
    /* preventDefault first: the fields sit in a real form now, so Enter
       would otherwise trigger the browser's own implicit submission — which
       is not subject to the e.repeat guard above, and a held Enter would be
       back to firing thirty submissions a second. Stopped here, then asked
       for deliberately on the line below. */
    if (e.key === 'Enter' && e.target.classList.contains('cza-in')) { e.preventDefault(); requestSubmit(); }
  });
  document.addEventListener('keydown', function (e) { if (isOpen && e.key === 'Escape') close(); });

  /* ========================================================= 6. INTERCEPT

     Capture phase on document, so this runs before any handler bound to the
     link itself — including the one inside the home scene's own section,
     whatever version of it happens to be deployed. That is the whole point:
     the gate no longer depends on any other file being current. */

  /* Gated by the attribute on the link, and only by that.

     Two earlier boundaries are worth recording, because the second one is
     what has just been removed:

       href contains '/products/' — wrong, because the destinations are
       merchant settings and can be any URL at all. When they were not
       product URLs the test never matched and every click went straight
       through, with the gate reporting itself perfectly healthy.

       anything inside #golden-fade-wrapper — correct while the home scene
       was behind the form, since every link out of it was a chapter
       destination. The scene is no longer gated, so this would now catch
       every route out of the home page and put the form back in front of a
       button that is meant to read "Discover" and navigate. Removed with it.

     One rule left: an element carrying data-czard-access-link opts in. It is
     an explicit, greppable mark on the few links that want the form, rather
     than a container whose contents can change under it. */
  function isGated (el) {
    return el.hasAttribute('data-czard-access-link');
  }

  document.addEventListener('click', function (e) {
    if (granted()) return;
    if (!e.target || !e.target.closest) return;

    var a = e.target.closest('[data-czard-access-link]');
    if (!a || !isGated(a)) return;

    var href = a.getAttribute('href');
    // Marked as gated but pointing nowhere — a link whose destination the
    // merchant never filled in. Nothing to hold, so nothing to ask for.
    if (!href || href === '#') {
      console.warn(
        '[czard-access] a gated link has no destination (href="#"), so the form ' +
        'was not opened. Set its link in the theme editor.', a
      );
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    // the event the scene's own handler used to send — stopping propagation
    // is precisely what prevents it sending it now
    track('Request Access Clicked', {
      'Button Name': (a.textContent || 'Request Access').trim(),
      'Page URL': location.href, Source: 'Website'
    });

    console.info('[czard-access] intercepted →', href);
    open(href, 'Scene link');
  }, true);

  /* ============================================================ 7. LABELS

     Enforced on an animation frame rather than written once, because a gated
     link's own section may repaint it from state that knows nothing about
     the unlock — so a one-shot write can lose. A string compare per frame is
     free, and only differs for a few frames after an unlock. */

  function unlockedLabelFor (el) {
    return el.getAttribute('data-access-unlocked') ||
           el.getAttribute('data-unlocked-label') ||
           'Discover';
  }

  function enforceLabels () {
    if (!granted()) return;
    // #discover-btn is deliberately absent: the home scene owns its own
    // label now, and it is the same string locked or unlocked.
    var els = document.querySelectorAll('[data-czard-access-link]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var target = el.querySelector('[data-czard-access-label]') || el;
      var want = unlockedLabelFor(el);
      if (target.textContent !== want) target.textContent = want;
    }
  }

  /* This was an unconditional requestAnimationFrame loop, running a
     querySelectorAll every frame for the lifetime of every page on the
     store — including the home page, where the frame budget is already
     spent on a WebGL scene, and including the great majority of pages that
     contain no gated button at all.

     It only ever needs to run just after something has repainted the
     button, so it runs in bursts instead: a burst is started by the events
     that can actually change the label, and stops itself once the settle
     window passes. Between bursts nothing runs at all.

     A burst cannot feed itself. enforceLabels only writes when the text
     differs, so our own write schedules exactly one more burst, which finds
     nothing to do and lets the frames stop. */
  var enforceUntil = 0, enforcing = false;

  function enforceBurst (ms) {
    if (!granted()) return;
    enforceUntil = Math.max(enforceUntil, Date.now() + (ms || 1200));
    if (enforcing) return;
    enforcing = true;
    (function frame () {
      enforceLabels();
      if (Date.now() < enforceUntil) requestAnimationFrame(frame);
      else enforcing = false;
    })();
  }

  // The unlock itself, and the moment the page is first usable.
  document.addEventListener(CHANGE_EVENT, function () { enforceBurst(3000); });
  enforceBurst(3000);

  /* A MutationObserver on #golden-fade-wrapper used to sit here, restarting
     the burst whenever the home scene repainted its pill — the one thing
     that could undo the label with no event this file could hear. The scene
     is no longer gated and no longer carries a label this file writes, so
     the observer watched a subtree it had no business in and rescheduled
     frames over a running WebGL scene for nothing. An unlock is once again
     the only trigger there is.

     ======================================================= 7b. SHEET RETRY

     The redirect after a submit is itself the next page load, so a lead that
     could not be confirmed gets its second attempt seconds later, on the
     collection page. pageshow covers the back/forward cache, where load does
     not fire at all. */
  flush();
  window.addEventListener('pageshow', function () { flush(); });

  /* ================================================================ 8. API */

  window.CzardAccess = {
    granted: granted,
    isOpen: function () { return isOpen; },
    open: open,
    close: close,
    profile: function () { var s = readState(); return s && { name: s.name, email: s.email, phone: s.phone }; },
    /* CzardAccess.probe() — what the gate sees right now. If a click is not
       being intercepted, this says why: gatedLinks empty means nothing on
       this page opts in, and an href of '#' means the link has no
       destination to hold.

       It used to report on #golden-fade-wrapper and #discover-btn, which was
       the boundary at the time. The home scene is no longer gated, so this
       reads the links that actually are: anything carrying
       data-czard-access-link, which today is the collection page's manifesto
       button. Nothing here reports on the home page any more — an empty
       gatedLinks there is the expected answer, not a fault. */
    probe: function () {
      var els = document.querySelectorAll('[data-czard-access-link]');
      var links = [];
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var label = el.querySelector('[data-czard-access-label]') || el;
        links.push({
          href: el.getAttribute('href'),
          label: (label.textContent || '').trim(),
          source: el.getAttribute('data-access-source') || '',
          gated: isGated(el),
          // a link the page has disabled is a click this file never sees
          pointerEvents: getComputedStyle(el).pointerEvents
        });
      }
      return {
        version: VERSION,
        granted: granted(),
        gatedLinks: links,
        modalMounted: !!veil.parentNode
      };
    },
    /* CzardAccess.we() — what WebEngage actually exposes on this page.
       hasUserSetAttribute is the one that decides whether user attributes
       can be written at all. */
    we: weReport,
    /* CzardAccess.sheet() — is the endpoint configured, and where from?
       CzardAccess.testSheet() — writes one test row immediately, with no
       form and no navigation, so the sheet can be checked on its own. It
       returns a promise: true means the POST reached the network, false
       means something blocked it and the row is now in the outbox.

       pendingRows above zero on a quiet page is the tell that sends are
       being blocked in this browser — that is the Brave case, and the only
       thing that reads it from the outside.

       For the Apps Script side, doPost should honour two fields, and they
       do different jobs:

         payload.id       one attempt. Stable across retries, so a row
                          re-POSTed from the outbox is dropped rather than
                          written twice.
         payload.user_id  the person — their WhatsApp number in E.164.
                          Drops the same visitor arriving again from another
                          browser or another device, which nothing in this
                          script can see.

       Read both columns once into a Set and return early on a hit. Without
       that, this file's guards still stop repeats from one browser, but not
       the same person on their phone.

       user_id is a leading-'+' string and Sheets treats a leading + as a
       formula, so write that column as text — setValue on a range whose
       number format is '@', or appendRow with "'" + user_id. Left as-is it
       is coerced to a number, and a number never matches the string the
       next POST sends, so the dedupe silently stops working.

       Implementing doGet as the same handler also turns on the image
       fallback, which is the one path left when POSTs are filtered. */
    sheet: function () {
      var scene = document.getElementById('golden-fade-wrapper');
      return {
        // first thing to read. A number below the deployed one means the
        // browser is serving a cached copy of this file and none of the
        // behaviour described here is actually running.
        version: VERSION,
        sceneFound: !!scene,
        attributePresent: !!(scene && scene.hasAttribute('data-czard-sheet')),
        rawAttribute: scene && scene.getAttribute('data-czard-sheet'),
        resolvedUrl: sheetUrl(),
        canFetch: typeof fetch === 'function',
        canSendBeacon: !!navigator.sendBeacon,
        pendingRows: outbox().length,
        rescueEnabled: rescueEnabled()
      };
    },
    // the unconfirmed leads, oldest first — what a blocked browser leaves behind
    outbox: outbox,
    flush: flush,
    /* CzardAccess.written() — everyone this browser has already put in the
       sheet, and whether the write was confirmed. Keyed on the WhatsApp
       number: a number in here is why a resubmit writes no second row.
       CzardAccess.forget('+919876543210') drops one, or the lot when called
       with nothing, for when a row genuinely needs writing again. */
    written: ledger,
    forget: function (phone) {
      if (!phone) { try { localStorage.removeItem(LEDGER_KEY); } catch (e) {} return {}; }
      var o = ledger();
      delete o[userId(phone)];
      ledgerSave(o);
      return o;
    },
    testSheet: function () {
      var self = this;
      /* Its own identity is forgotten first, or the ledger suppresses every
         run after the first and the test reports a success that never
         touched the network. The number, not the address — the ledger keys
         on user_id now, and forgetting the wrong one would have made this
         fail exactly that way. Named once so the two cannot drift. */
      var TEST_PHONE = '+919999999999';
      this.forget(TEST_PHONE);
      return toSheet({ name: 'Test Visitor', email: 'czard-test@example.com',
                       phone: TEST_PHONE, consent: true })
        .then(function (confirmed) {
          var s = self.sheet();
          s.confirmed = confirmed;
          console.info('[czard-access] testSheet →', s);
          return s;
        });
    },
    /* CzardAccess.testSend() — fires a real identify + event with dummy
       values, without touching the unlock state, so WebEngage delivery can
       be checked on its own. Look for the address in WebEngage → Users. */
    testSend: function () {
      var p = { name: 'Test Visitor', email: 'czard-test@example.com', phone: '+919999999999' };
      identify(p);
      track('Request Access Submitted', {
        Name: p.name, Email: p.email, WhatsApp: p.phone, Source: 'Console test'
      });
      track('Request_access_clicked_submitted', {
        Name: p.name, Email: p.email, WhatsApp: p.phone, Source: 'Console test'
      });
      return weReport();
    },
    testUnlock: function () {
      writeState({ name: 'Test Visitor', email: 'test@example.com', phone: '+919999999999' });
      return { granted: granted() };
    },
    /* Back to a first-time visitor, ledger included — otherwise the next
       test submission is deduped away and looks like a broken sheet. The
       outbox is deliberately left alone: it may still hold a real lead that
       has not landed yet, and resetting the gate is no reason to lose it. */
    reset: function () {
      cached = null;
      try { localStorage.removeItem(STORE_KEY); } catch (e) {}
      try { localStorage.removeItem(LEDGER_KEY); } catch (e) {}
      try { document.cookie = COOKIE_NAME + '=; Max-Age=0; path=/'; } catch (e) {}
      location.reload();
    },
    CHANGE_EVENT: CHANGE_EVENT
  };

  console.info('[czard-access] v' + VERSION + ' self-contained | granted:', granted());
})();
